import { HTMLSelect } from "@blueprintjs/core";
import { FocusHelp, FormGroup, Icon, InputGroup, TextArea } from "@focus/focusUI";
import {
  FocusFormBuilder,
  type ComponentSchema,
  type FormData,
} from "@focus/formComponents";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import type { ContactFormState } from "../types/questionnaires";
import { toFocusFormErrors } from "../utils/forms";
import help from "../utils/markdown/OperatorConfigCSV.md?raw";
import ErrorsCollapse from "./Settings/OperatorConfig/ErrorsCollapse";
import CsvImportButton from "./upload/CSVImportButton";

type ContactInquiryData =
  | {
      message: string;
      formDetails?: never;
    }
  | {
      formDetails: FormData;
      message?: never;
    };

type ContactFormInitialData = Omit<
  ContactFormState,
  "message" | "formDetails"
> &
  ContactInquiryData;

interface ContactFormProps {
  /**
   * The title to display at the top of the form, indicating the purpose of the form and encouraging the user to fill it out.
   */
  title: string;
  /**
   * A brief reason or instruction to display at the top of the form, guiding the user on what the form is for and how to fill it out.
   */
  reason: string;
  /**
   * Initial data to pre-fill the form. This includes the user's name, email, organization, selected service, and either a message or structured form details depending on the mode of the form.
   */
  initialData: ContactFormInitialData;
  /**
   * Options for the "Service" dropdown in the form. Each option should have a label (what the user sees) and a value (what is sent in the inquiry).
   */
  contactServiceOptions: { label: string; value: string }[];
  /**
   * If true, the form will not render the Service selector and the generated inquiry will omit the service line.
   */
  hideService?: boolean;
  /**
   * If true, the introductory left panel is hidden so the form can be embedded in a larger layout.
   */
  /**
   * An optional JSON schema that defines the structure of the form details. If provided, the form will render a dynamic form based on this schema instead of a simple message textarea.
   */
  schema?: ComponentSchema;
  /**
   * A callback function to handle the form submission. If not provided, the form will default to sending an email using the built-in mailto functionality.
   */
  handleSubmit?: (data: ContactFormState) => void | Promise<void>;
  /**
   * If true, the user will be prompted to confirm before submitting the form.
   */
  confirmOnSubmit?: boolean;
  /**
   * A callback function to validate the form data. If not provided, no validation will be performed.
   */
  validate?: (
    data: FormData | null,
    schema: ComponentSchema,
  ) => Record<string, string>;
  /**
   * Show upload CSV button
   */
  uploadButton?: boolean;
  handleUpload?: (file: File) => any;
}
const contactEmail = "admin-ssp@ops.spacesafetyportal.org";

const getQuestionnaireErrors = (
  data: FormData | null | undefined,
  schema: ComponentSchema | undefined,
  validate: ContactFormProps["validate"],
) => {
  if (!schema || !validate) {
    return {};
  }

  return validate(data ?? null, schema);
};

const buildContactMailto = (
  {
    email,
    name,
    organization,
    service,
    message,
    formDetails,
  }: ContactFormState,
  hideService?: boolean,
) => {
  const subject = hideService
    ? "Contact inquiry"
    : `Additional services inquiry - ${service}`;
  const inquiryBody = message ?? JSON.stringify(formDetails, null, 2);

  const body = [
    `Name: ${name}`,
    `Organization: ${organization || "-"}`,
    `Email: ${email}`,
    ...(hideService ? [] : [`Service: ${service}`]),
    "",
    "Message:",
    inquiryBody,
  ].join("\n");

  return `mailto:${contactEmail}?subject=${encodeURIComponent(
    subject,
  )}&body=${encodeURIComponent(body)}`;
};

/**
 * QuestionnaireFormView component
 */
export const QuestionnaireForm = ({
  initialData,
  contactServiceOptions,
  hideService,
  schema,
  reason,
  handleSubmit,
  confirmOnSubmit,
  validate,
  title,
  uploadButton,
  handleUpload,
}: ContactFormProps) => {
  const [form, setForm] = useState<ContactFormState>(initialData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setErrors(getQuestionnaireErrors(form.formDetails, schema, validate));
  }, [form.formDetails, schema, validate]);

  const mailtoHref = buildContactMailto(form, hideService);
  const formErrors = useMemo(() => toFocusFormErrors(errors), [errors]);
  /**
   * Updates the form state for a given field and value.
   * @param field - The field to update
   * @param value - The new value for the field
   */
  const updateForm = <K extends keyof ContactFormState>(
    field: K,
    value: ContactFormState[K],
  ) => {
    setForm((current) => ({ ...current, [field]: value }));
  };
  const hasErrors = Object.keys(errors).length > 0;
  const canSubmit = !hasErrors && !isSubmitting;

  const handleFileSelected = async (file: File, field: string) => {
    const data = await handleUpload(file);
    setForm((current) => ({ ...current, [field]: data }));
  };
  const handleFormSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const currentErrors = getQuestionnaireErrors(
      form.formDetails,
      schema,
      validate,
    );
    if (Object.keys(currentErrors).length > 0) {
      setErrors(currentErrors);
      return;
    }

    if (confirmOnSubmit) {
      const userConfirmed = window.confirm(
        "Are you sure you want to submit the inquiry?",
      );
      if (!userConfirmed) {
        return;
      }
    }

    try {
      setIsSubmitting(true);
      if (handleSubmit) {
        await handleSubmit(form);
      } else {
        window.location.href = mailtoHref;
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="operator-config-questionnaire-form flex h-full w-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
      <div className="grid h-full w-full min-h-0 min-w-0 flex-1 grid-cols-1 grid-rows-[auto_minmax(0,1fr)] gap-0 lg:grid-cols-[minmax(280px,0.8fr)_minmax(420px,1.2fr)] lg:grid-rows-1">
        <div className="focusoc-theme-surface flex min-h-0 flex-col justify-between overflow-y-auto border-b border-slate-200 p-5 lg:border-b-0 lg:border-r">
          <div>
            <div className="text-xs font-semibold uppercase opacity-75">
              Contact
            </div>
            <h2 className="mt-2 text-xl font-bold">{title}</h2>
            <p className="mt-3 text-sm font-medium leading-6 opacity-80">
              {reason} The form opens a pre-filled email so the inquiry can be
              sent from your email client.
            </p>
            <a
              className="mt-3 flex w-fit min-h-10 items-center rounded-lg border border-white/20 !bg-white px-4 text-sm font-bold !text-slate-950 shadow-sm transition hover:!bg-slate-100 hover:!text-slate-950 hover:no-underline"
              href={`mailto:${contactEmail}`}
              style={{ backgroundColor: "white", color: "rgb(15 23 42)" }}
            >
              {contactEmail}
            </a>
          </div>
          <div className="header-help-disclaimer flex items-start gap-3 rounded-xl border p-4 text-sm font-medium leading-6 shadow-sm">
            <span className="mt-0.5 flex shrink-0 items-center justify-center text-sky-100">
              <Icon icon="info-sign" title={false} />
            </span>
            <div className="min-w-0">
              Notice: for support-related issues, please contact Nuntiare via
              Support in the info menu. For analyses, questions, or general
              inquiries, please complete this form.
            </div>
          </div>
        </div>

        <form
          className="focusoc-form-shell flex min-h-0 min-w-0 flex-col gap-4 overflow-y-auto p-5"
          onSubmit={handleFormSubmit}
        >
          <ErrorsCollapse errors={errors} />
          <div className="grid shrink-0 grid-cols-1 gap-4 md:grid-cols-2">
            <FormGroup label="Name" labelFor="additional-service-name">
              <InputGroup
                id="additional-service-name"
                onChange={(event) => updateForm("name", event.target.value)}
                placeholder="Your name"
                required
                value={form.name}
              />
            </FormGroup>

            <FormGroup label="Email" labelFor="additional-service-email">
              <InputGroup
                id="additional-service-email"
                onChange={(event) => updateForm("email", event.target.value)}
                placeholder="you@example.com"
                required
                type="email"
                value={form.email}
              />
            </FormGroup>

            <FormGroup
              label="Organization"
              labelFor="additional-service-organization"
            >
              <InputGroup
                id="additional-service-organization"
                onChange={(event) =>
                  updateForm("organization", event.target.value)
                }
                placeholder="Operator / company"
                value={form.organization}
              />
            </FormGroup>

            {!hideService && (
              <FormGroup label="Service" labelFor="additional-service-type">
                <HTMLSelect
                  fill
                  id="additional-service-type"
                  onChange={(event) =>
                    updateForm("service", event.target.value)
                  }
                  options={contactServiceOptions}
                  value={form.service}
                />
              </FormGroup>
            )}
          </div>
          {!schema ? (
            <FormGroup
              className="additional-services-inquiry-field"
              label="Inquiry"
              labelFor="additional-service-message"
            >
              <TextArea
                className="additional-services-inquiry-textarea"
                fill
                id="additional-service-message"
                onChange={(event) => updateForm("message", event.target.value)}
                placeholder="Describe the service, fleet size, timeframe or any operational detail you want us to consider."
                required
                rows={8}
                value={form.message ?? ""}
              />
            </FormGroup>
          ) : (
            <div className="min-h-0 w-full overflow-auto">
              <span className="font-semibold text-slate-950">
                Required fields
              </span>
              <FocusFormBuilder
                errors={formErrors}
                formData={form.formDetails}
                schema={schema}
                uiSchema={undefined}
                onChange={(formData) => {
                  updateForm("formDetails", formData);
                }}
              />
            </div>
          )}

          <div className="flex w-full shrink-0 flex-wrap flex-col gap-3">
            <div className="w-full flex justify-between">
              {uploadButton && schema && (
                <div className="flex items-center">
                  <CsvImportButton
                    className={`inline-flex min-h-10 items-center justify-center !rounded-lg px-4 text-sm font-bold shadow-sm transition`}
                    handleAdd={(file) =>
                      handleFileSelected(file, "formDetails")
                    }
                  />
                  <FocusHelp helpText={help} />
                </div>
              )}
              <button
                className={`focusoc-theme-primary inline-flex min-h-10 items-center justify-center rounded-lg border px-4 text-sm font-bold shadow-sm transition ${
                  canSubmit ? "cursor-pointer" : "cursor-not-allowed opacity-50"
                }`}
                disabled={!canSubmit}
                type="submit"
              >
                {isSubmitting ? "Sending..." : "Send inquiry"}
              </button>
            </div>
            <div>
              <span className="text-xs font-semibold text-slate-500">
                The inquiry will be sent to {contactEmail}.
              </span>
            </div>
          </div>
        </form>
      </div>
    </section>
  );
};
